import React, { useState, useEffect } from 'react';
import { Crown, Check, Sparkles, Zap, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState({});
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const { user } = useAuthStore();

  useEffect(() => {
    fetchPlans();
    if (user?.role === 'artist') {
      fetchCurrentSubscription();
    }
  }, [user]);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/api/subscriptions/plans');
      setPlans(response.data);
    } catch (error) {
      setPlans({
        free: { price: 0, features: ['Basic profile', '5 bookings/month'] },
        basic: { price: 9.99, features: ['Enhanced profile', '20 bookings/month', 'Priority support'] },
        premium: { price: 29.99, features: ['Featured listing', 'Unlimited bookings', 'Analytics dashboard', 'Verified badge'] },
        enterprise: { price: 99.99, features: ['All premium features', 'Dedicated account manager', 'Custom branding', 'API access'] }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await api.get('/api/artist/subscription');
      setCurrentPlan(response.data);
    } catch (error) {
      setCurrentPlan({ plan: 'free' });
    }
  };

  const subscribeToPlan = async (planName) => {
    if (!user) {
      toast.error('Please sign in first');
      return;
    }
    if (user.role !== 'artist') {
      toast.error('Only artists can subscribe to plans');
      return;
    }
    setSubscribing(true);
    try {
      await api.post(`/api/artist/subscription?plan=${planName}`);
      toast.success(`Successfully subscribed to ${planName} plan!`);
      fetchCurrentSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName) {
      case 'free': return <Zap className="w-8 h-8" />;
      case 'basic': return <Star className="w-8 h-8" />;
      case 'premium': return <Crown className="w-8 h-8" />;
      case 'enterprise': return <Sparkles className="w-8 h-8" />;
      default: return <Zap className="w-8 h-8" />;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName) {
      case 'free': return 'border-gray-500';
      case 'basic': return 'border-blue-500';
      case 'premium': return 'border-purple-500 ring-2 ring-purple-500/50';
      case 'enterprise': return 'border-yellow-500';
      default: return 'border-gray-500';
    }
  };

  const getPlanGradient = (planName) => {
    switch (planName) {
      case 'free': return 'from-gray-600 to-gray-500';
      case 'basic': return 'from-blue-600 to-blue-500';
      case 'premium': return 'from-purple-600 to-pink-500';
      case 'enterprise': return 'from-yellow-600 to-orange-500';
      default: return 'from-gray-600 to-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Artist Subscription Plans</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Choose the plan that best fits your needs. Upgrade anytime to unlock more features and grow your booking business.
          </p>
        </div>

        {currentPlan && (
          <div className="mb-8 text-center">
            <p className="text-gray-400">
              Current Plan: <span className="text-purple-400 font-semibold capitalize">{currentPlan.plan}</span>
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(plans).map(([planName, planData]) => (
            <div
              key={planName}
              className={`rounded-2xl border ${getPlanColor(planName)} bg-white/5 backdrop-blur-sm overflow-hidden ${planName === 'premium' ? 'transform lg:-translate-y-4' : ''}`}
            >
              {planName === 'premium' && (
                <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-center py-2 text-sm font-medium">
                  Most Popular
                </div>
              )}
              <div className="p-6">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${getPlanGradient(planName)} flex items-center justify-center text-white mb-4 mx-auto`}>
                  {getPlanIcon(planName)}
                </div>
                <h3 className="text-xl font-bold text-white text-center capitalize mb-2">{planName}</h3>
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold text-white">${planData.price}</span>
                  {planData.price > 0 && <span className="text-gray-400">/month</span>}
                </div>
                <ul className="space-y-3 mb-6">
                  {planData.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => subscribeToPlan(planName)}
                  disabled={subscribing || currentPlan?.plan === planName}
                  className={`w-full ${planName === 'premium' ? 'bg-gradient-to-r from-purple-600 to-pink-500' : ''}`}
                  variant={planName === 'premium' ? 'default' : 'outline'}
                >
                  {currentPlan?.plan === planName ? 'Current Plan' : subscribing ? 'Processing...' : 'Get Started'}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            All plans include secure payment processing and 24/7 platform access.
            <br />Need a custom solution? <a href="/help" className="text-purple-400 hover:underline">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;